import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
export function HospitalityHero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  useEffect(() => {
    // Play the video only when it scrolls into view
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        videoRef.current?.play().catch(() => {});
      } else {
        videoRef.current?.pause();
      }
    }, {
      threshold: 0.2
    });
    if (videoRef.current) {
      observer.observe(videoRef.current);
    }
    return () => observer.disconnect();
  }, []);
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  const scrollToEOI = () => {
    const element = document.getElementById('eoi-form');
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative pt-32 md:pt-48 pb-20 overflow-hidden" data-id="element-486">
      {/* Ambient Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-brand-orange/10 rounded-full blur-[120px] pointer-events-none" data-id="element-487" />

      <div className="relative z-20 max-w-5xl mx-auto px-6 text-center mb-20" data-id="element-488">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-8 flex justify-center" data-id="element-489">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-brand-orange/30 backdrop-blur-md" data-id="element-490">
            <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" data-id="element-491" />
            <span className="text-xs font-medium text-gray-300 uppercase tracking-[0.2em]" data-id="element-492">
              Launching Soon
            </span>
          </div>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-6xl md:text-8xl lg:text-9xl font-display mb-8 tracking-tight leading-[0.9] text-balance text-white" data-id="element-493">
          The AI Operating System <br className="hidden md:block" data-id="element-494" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-300 via-gray-400 to-gray-600 relative inline-block" data-id="element-495">
            for Hospitality.
            <motion.span className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-orange/30 to-transparent blur-md mix-blend-overlay" animate={{
            x: ['-100%', '200%']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            repeatDelay: 2
          }} data-id="element-496" />
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed font-light" data-id="element-497">
          Aijentik Hospitality is building the next generation AI platform for
          venues, groups and hospitality teams. Intelligent agents, live
          operational visibility, automated guest engagement and AI-powered
          venue management in one unified experience.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12" data-id="element-498">
          <button onClick={scrollToEOI} className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-10 py-5 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] text-lg" data-id="element-499">
            Register Your Interest
          </button>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.6
      }} className="flex flex-col items-center" data-id="element-500">
          <div className="flex -space-x-3 mb-4" data-id="element-501">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-brand-black bg-gray-800" data-id="element-502" />)}
          </div>
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-[0.25em]" data-id="element-503">
            Trusted by forward-thinking hospitality leaders
          </p>
        </motion.div>
      </div>

      {/* Cinematic Video Section */}
      <motion.div initial={{
      opacity: 0,
      y: 40,
      scale: 0.95
    }} whileInView={{
      opacity: 1,
      y: 0,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 1,
      ease: [0.16, 1, 0.3, 1]
    }} className="relative max-w-6xl mx-auto px-6 z-20" data-id="element-504">
        <div className="relative rounded-2xl md:rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_0_80px_rgba(219,127,74,0.15)] aspect-video bg-[#050505] group" data-id="element-505">
          <video ref={videoRef} muted={isMuted} loop playsInline className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" data-id="element-506">
            <source src="https://cdn.magicpatterns.com/uploads/wqbYMzCS8CkRBzS4QDHGkE/aijentik-cinematic-final-v2.mp4" type="video/mp4" data-id="element-507" />
          </video>

          {/* Sound Toggle Button */}
          <button onClick={toggleMute} className="absolute bottom-6 right-6 z-30 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-white hover:bg-black/60 hover:scale-105 transition-all duration-300 shadow-lg" aria-label={isMuted ? 'Unmute video' : 'Mute video'} data-id="element-508">
            {isMuted ? <VolumeX className="w-5 h-5 text-gray-300" data-id="element-509" /> : <Volume2 className="w-5 h-5 text-white" data-id="element-510" />}
          </button>

          {/* Subtle glass overlay on the edges */}
          <div className="absolute inset-0 rounded-2xl md:rounded-[2.5rem] ring-1 ring-inset ring-white/10 pointer-events-none" data-id="element-511" />
        </div>
      </motion.div>
    </section>;
}